import { useState } from 'react';
import { FiUpload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import FileUpload from '../../molecules/FileUpload';
import { contractApi } from '../../../services/contractApi';
import { contractsManagementApi } from '../../../services/contractsManagementApi';
import type { OperationResult } from '../../../services/contractsManagementApi';
import { toBase64 } from '../../../utils/fileUtils';
import { mapOperationTypeToContractType } from './ContractSignPage.helpers';

interface UploadStepProps {
    onComplete: (documentId: string, contractId: string, fileName: string, operationNumber: string) => void;
    setLoading: (v: boolean) => void;
}

const UploadStep = ({ onComplete, setLoading }: UploadStepProps) => {
    const [file, setFile] = useState<File | null>(null);
    const [operationNumber, setOperationNumber] = useState('');
    const [operationResults, setOperationResults] = useState<OperationResult[]>([]);
    const [selectedOperation, setSelectedOperation] = useState<OperationResult | null>(null);
    const [searchingOperations, setSearchingOperations] = useState(false);
    const [uploading, setUploading] = useState<boolean>(false);

    const searchOperations = async (value: string) => {
        setOperationNumber(value);
        setSelectedOperation(null);
        if (value.length < 2) { setOperationResults([]); return; }
        setSearchingOperations(true);
        try {
            const results = await contractsManagementApi.searchReceivableAssignments(value);
            setOperationResults(results);
        } catch { setOperationResults([]); }
        finally { setSearchingOperations(false); }
    };

    const handleUpload = async () => {
        if (!file) { toast.warn('Selecione um PDF'); return; }
        setUploading(true);
        setLoading(true);
        try {
            const base64 = await toBase64(file);
            const contractType = mapOperationTypeToContractType(selectedOperation?.operation_type || '') || 'CPR_F';

            const docResult = await contractApi.createDocument(file.name, base64, contractType, operationNumber);

            onComplete(docResult.documentId, docResult.contractId, file.name, operationNumber);
            toast.success('Documento enviado');
        } catch (e: any) {
            toast.error(e.message || 'Erro ao enviar documento');
        } finally {
            setUploading(false);
            setLoading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><FiUpload /> Upload do Documento</h2>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número da Operação</label>
                    <div className="relative">
                        <input type="text" value={operationNumber} onChange={e => searchOperations(e.target.value)}
                            placeholder="Digite para buscar..."
                            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 ${selectedOperation ? 'border-green-400' : 'border-gray-300 dark:border-slate-600'}`} />
                        {searchingOperations && <span className="absolute right-3 top-2.5 text-xs text-gray-400">Buscando...</span>}
                        {selectedOperation && <span className="absolute right-3 top-2.5 text-green-600">✓</span>}
                        {operationResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {operationResults.map(op => (
                                    <button key={op.credit_operation_id} onClick={() => { setOperationNumber(op.operation_number); setSelectedOperation(op); setOperationResults([]); }}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-slate-700 last:border-0">
                                        <span className="font-medium text-gray-900 dark:text-white">{op.operation_number}</span>
                                        <span className="ml-2 text-gray-400 text-xs">{op.status}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <FileUpload id="contract-pdf" name="document" label="Arquivo:" accept=".pdf" info="Somente arquivos PDF" errorMessage="" onUploadFile={(f) => setFile(f)} onRemoveFile={() => setFile(null)} />
                <button onClick={handleUpload} disabled={!file || uploading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    Enviar e Extrair Dados
                </button>
            </div>
        </div>
    );
};

export default UploadStep;
